import styled from 'styled-components';
import Pagination from '../../components/pagination/Pagination';
import { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import GameService from '../../../service/game/GameService';
import InterestService from '../../../service/Interest';
import { useModal } from '../../../context/ModalContext';
import AddInterestItemModal from '../../components/modal/AddInterestItem';

type WinningItem = {
  iidx: number;
  caseno: string;
  court: string;
  category: string;
  address: string;
  appraisalPrice: string;
  minPrice: string;
  buildingArea: number;
  landArea: number;
  status: string;
  tag: string[];
  thumb: string;
  views: number;
  is_like: boolean;
  win_cnt?: string;
  dealPrice: number;
  buildingName?: string;
};

type MatchItem = {
  itemId: number;
  category: string;
  address: string;
  iidx: number;
  is_like: boolean;
  buildingName?: string;
};

type Match = {
  match_no: number;
  round_no: number;
  winner_item_id: number;
  left_item: MatchItem;
  right_item: MatchItem;
  left_item_id: number;
  right_item_id: number;
};
interface FullLankingProps {
  isLogin: () => boolean;
}
const FullLanking = ({ isLogin }: FullLankingProps) => {
  const { gameId, playerId, playId } = useParams<{
    gameId: string;
    playId: string;
    playerId: string;
  }>();
  const [selectedIdx, setSelectedIdx] = useState<number | null>(null);
  const [showLikeModal, setShowLikeModal] = useState(false);
  const pageSize = 5;
  const [currentPage, setCurrentPage] = useState(1);
  const [items, setItems] = useState<WinningItem[]>([]);
  const [total, setTotal] = useState(0);
  const [matchResults, setMatchResults] = useState<Match[]>([]);
  const [expandedTags, setExpandedTags] = useState<{ [key: number]: boolean }>(
    {},
  );
  const [likedMap, setLikedMap] = useState<{ [key: number]: boolean }>({});
  const { modalDispatch } = useModal();
  useEffect(() => {
    if (!gameId || !playId || !playerId) return;

    const fetchAllMatchResults = async () => {
      try {
        const maxRounds = 4;
        const allRounds = await Promise.all(
          Array.from({ length: maxRounds }, (_, i) =>
            GameService.getMatch(
              Number(gameId),
              Number(playId),
              i + 1,
              playerId,
            ),
          ),
        );

        const merged = allRounds.flatMap((res) => res.data || []);
        setMatchResults(merged);

        const finalMatch = merged.reduce((prev, curr) =>
          prev.round_no > curr.round_no ? prev : curr,
        );
        const winnerItemId = finalMatch?.winner_item_id;

        const allItems = merged.flatMap((match) =>
          [match.left_item, match.right_item].filter(Boolean),
        );

        const itemIdToIidxMap = new Map<number, number>();
        allItems.forEach((item) => {
          itemIdToIidxMap.set(item.itemId, item.iidx);
        });
        const winnerIidx = itemIdToIidxMap.get(winnerItemId);

        const uniqueMap = new Map<number, WinningItem>();
        allItems.forEach((item) => {
          if (!uniqueMap.has(item.iidx)) {
            uniqueMap.set(item.iidx, item as WinningItem);
          }
        });

        const itemToMaxRound = new Map<number, number>();
        merged.forEach((match) => {
          const { left_item, right_item, round_no, winner_item_id } = match;

          if (left_item) {
            const prev = itemToMaxRound.get(left_item.iidx) ?? 0;
            itemToMaxRound.set(left_item.iidx, Math.max(prev, round_no));
          }

          if (right_item) {
            const prev = itemToMaxRound.get(right_item.iidx) ?? 0;
            itemToMaxRound.set(right_item.iidx, Math.max(prev, round_no));
          }

          const loserId =
            left_item?.itemId === winner_item_id
              ? right_item?.iidx
              : left_item?.iidx;
          if (loserId !== undefined) {
            const prev = itemToMaxRound.get(loserId) ?? 0;
            itemToMaxRound.set(loserId, Math.max(prev, round_no));
          }
        });

        const sortedItems = Array.from(uniqueMap.values()).sort((a, b) => {
          if (a.iidx === winnerIidx) return -1;
          if (b.iidx === winnerIidx) return 1;

          const aRound = itemToMaxRound.get(a.iidx) ?? 0;
          const bRound = itemToMaxRound.get(b.iidx) ?? 0;
          return bRound - aRound;
        });

        const start = (currentPage - 1) * pageSize;
        const end = start + pageSize;
        const pagedItems = sortedItems.slice(start, end);

        setItems(pagedItems);
        setTotal(sortedItems.length);
      } catch (err) {
        console.error('[❌ 전체 경기 기록 조회 실패]', err);
      }
    };

    fetchAllMatchResults();
  }, [gameId, playId, playerId, currentPage]);

  const totalPages = Math.ceil(total / pageSize);
  const toggleTags = (index: number) => {
    setExpandedTags((prev: { [key: number]: boolean }) => ({
      ...prev,
      [index]: !prev[index],
    }));
  };
  useEffect(() => {
    const newLikedMap: { [key: number]: boolean } = {};
    items.forEach((item) => {
      const matched = matchResults.find(
        (m) =>
          m.left_item?.iidx === item.iidx || m.right_item?.iidx === item.iidx,
      );

      const matchedItem =
        matched?.left_item?.iidx === item.iidx
          ? matched.left_item
          : matched?.right_item?.iidx === item.iidx
            ? matched.right_item
            : null;

      newLikedMap[item.iidx] = matchedItem?.is_like ?? false;
    });
  }, [items, matchResults]);
  const disLike = async (iidx: number) => {
    try {
      await InterestService.dislike(iidx);
      alert('관심물건에서 삭제되었습니다.');
      setItems((prev) =>
        prev.map((item) =>
          item.iidx === iidx ? { ...item, is_like: false } : item,
        ),
      );
    } catch (error) {
      console.error('관심물건 삭제 실패:', error);
      alert('관심물건 삭제 중 오류가 발생했습니다.');
    }
  };
  const toggleLike = (iidx: number, liked: boolean) => {
    setItems((prev) =>
      prev.map((item) =>
        item.iidx === iidx ? { ...item, is_like: liked } : item,
      ),
    );
  };
  const [expandedRows, setExpandedRows] = useState<Set<number>>(new Set());
  const toggleExpand = (key: number) => {
    setExpandedRows((prev) => {
      const next = new Set(prev);
      next.has(key) ? next.delete(key) : next.add(key);
      return next;
    });
  };
  const renderTags = (
    status: string,
    tags: string[],
    key: number,
    max: number = 2,
  ) => {
    const combined = [
      ...(status ? [`__STATUS__${status}`] : []),
      ...(tags ?? []),
    ];
    const isExpanded = expandedRows.has(key);
    const visible = isExpanded ? combined : combined.slice(0, max);
    const hiddenCount = isExpanded ? 0 : Math.max(combined.length - max, 0);

    return (
      <>
        {visible.map((tag, i) =>
          tag.startsWith('__STATUS__') ? (
            <TagRed key={`${key}-${i}`}>{tag.replace('__STATUS__', '')}</TagRed>
          ) : tag.includes('대항력') ? (
            <TagBlue key={`${key}-${i}`}>{tag}</TagBlue>
          ) : (
            <TagGray key={`${key}-${i}`}>{tag}</TagGray>
          ),
        )}
        {hiddenCount > 0 && (
          <TagGray
            onClick={(e) => {
              e.stopPropagation();
              toggleExpand(key);
            }}
            style={{ cursor: 'pointer' }}
          >
            +{hiddenCount}개
          </TagGray>
        )}
        {isExpanded && (
          <TagGray
            onClick={(e) => {
              e.stopPropagation();
              toggleExpand(key);
            }}
            style={{ cursor: 'pointer' }}
          >
            접기
          </TagGray>
        )}
      </>
    );
  };

  return (
    <Container>
      <Inner>
        <Content id="list-top">
          <Title>내 게임 결과</Title>
          <TableList>
            {items.map((item, index) => {
              const rankIndex = (currentPage - 1) * pageSize + index + 1;

              // 이 아이템이 참여한 모든 경기 찾기
              const myMatches = matchResults.filter(
                (m) =>
                  m.left_item?.iidx === item.iidx ||
                  m.right_item?.iidx === item.iidx,
              );

              // 제일 마지막 경기 (라운드 번호 가장 큰 것)
              const myMatch =
                myMatches.length > 0
                  ? myMatches.reduce((prev, curr) =>
                      prev.round_no > curr.round_no ? prev : curr,
                    )
                  : null;

              // 라운드 라벨
              const getEliminationRound = (iidx: number): number => {
                const myMatches = matchResults.filter(
                  (m) =>
                    m.left_item?.iidx === iidx || m.right_item?.iidx === iidx,
                );

                if (myMatches.length === 0) return 0;

                const lastMatch = myMatches.reduce((prev, curr) =>
                  prev.round_no > curr.round_no ? prev : curr,
                );

                const myItem =
                  lastMatch.left_item?.iidx === iidx
                    ? lastMatch.left_item
                    : lastMatch.right_item;
                const myItemId = myItem?.itemId;

                const didWin = lastMatch.winner_item_id === myItemId;

                return didWin ? lastMatch.round_no + 1 : lastMatch.round_no;
              };

              const getResultLabel = (round: number): string => {
                if (round >= 4) return '우승';
                if (round === 3) return '결승 탈락';
                if (round === 2) return '준결승 탈락';
                if (round === 1) return '8강 탈락';
                return '탈락';
              };
              const eliminatedRound = getEliminationRound(item.iidx);
              const label = getResultLabel(eliminatedRound);

              let opponentText: string | null = null;
              if (myMatch && eliminatedRound < 5) {
                const opponent =
                  myMatch.left_item?.iidx === item.iidx
                    ? myMatch.right_item
                    : myMatch.left_item;

                if (opponent) {
                  opponentText = `VS ${opponent.buildingName || opponent.address}`;
                }
              }
              return (
                <TableItem
                  key={item.iidx}
                  rankIndex={rankIndex}
                  currentPage={currentPage}
                >
                  <InfoBox>
                    <FlexBox>
                      <Lable>
                        {label === '우승' ? (
                          <img
                            src="/assets/images/game/win_label.svg"
                            alt="우승"
                          />
                        ) : (
                          <>
                            <Result>{label}</Result>
                            {opponentText && <span>{opponentText}</span>}
                          </>
                        )}
                      </Lable>
                      <img
                        src={`/assets/images/game/${item.is_like ? 'heart_on' : 'heart_off'}.svg`}
                        onClick={(e) => {
                          e.stopPropagation();

                          if (!isLogin()) {
                            alert('로그인 후 이용해주세요.');
                            return;
                          }

                          if (!item.iidx) return;

                          if (item.is_like) {
                            disLike(item.iidx);
                          } else {
                            setSelectedIdx(item.iidx);
                            setShowLikeModal(true); // 모달 내부에서 toggleLike 호출됨
                          }
                        }}
                        style={{ cursor: 'pointer' }}
                        alt="관심 등록 버튼"
                      />
                    </FlexBox>
                    <TitleInfo>
                      <Rank>
                        {currentPage === 1 && index < 3 ? (
                          <img
                            src={`/assets/images/icon/lank_medal_${index + 1}.svg`}
                            alt={`메달 ${index + 1}`}
                          />
                        ) : (
                          <RankIndex>{rankIndex}</RankIndex>
                        )}
                      </Rank>
                      {item.buildingName || item.address}
                    </TitleInfo>
                    <FlexBox02>
                      <Local>
                        <span>{item.category}</span>
                        <Cicle>・</Cicle>
                        {item.address}
                        <Cicle>・</Cicle>
                        {(Number(item.dealPrice) / 100000000).toFixed(1)} 억원
                        <Cicle>・</Cicle>
                        {item.buildingArea}m² (
                        {Math.round(item.buildingArea / 3.3)}평)
                      </Local>
                      <TagWrap>
                        {renderTags(item.status, item.tag || [], item.iidx, 2)}
                      </TagWrap>
                    </FlexBox02>
                  </InfoBox>

                  <MoveBtn
                    rankIndex={rankIndex}
                    onClick={() => {
                      if (!isLogin()) {
                        modalDispatch({
                          type: 'USER_ALERT',
                          payload: 'guest',
                        });
                        return;
                      }
                      window.open(`/auction/detail/${item.iidx}`, '_blank');
                    }}
                  >
                    경매 물건 상세보기
                    <img
                      src={`/assets/images/game/${
                        rankIndex === 3 ? 'move_icon_white' : 'move_icon'
                      }.svg`}
                    />
                  </MoveBtn>
                </TableItem>
              );
            })}
          </TableList>
          <PageWrap>
            <Pagination
              currentPage={currentPage}
              totalPages={totalPages}
              onPageChange={(page) => setCurrentPage(page)}
              scrollTargetId="list-top"
            />
          </PageWrap>
        </Content>
      </Inner>
      {showLikeModal && selectedIdx !== null && (
        <AddInterestItemModal
          setShowLikeModal={setShowLikeModal}
          selectedIdx={selectedIdx}
          mode="add"
          selectedItem={{
            iidx: selectedIdx,
            group: '',
            group_idx: 0,
            level: 0,
            memo: '',
          }}
          setActiveItem={() => {}}
          checkIsLikeItem={(iidx: number) => toggleLike(iidx, true)}
        />
      )}
    </Container>
  );
};

export default FullLanking;

const Container = styled.div`
  width: 100%;
  background: #f3f4f6;
  flex-direction: row;
  justify-content: center;
`;
const Inner = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
`;
const Content = styled.div`
  padding: 40px 20px;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 24px;
  align-self: stretch;
  width: 100%;
`;

const Title = styled.div`
  color: #000;
  font-family: 'Spoqa Han Sans Neo';
  font-size: 18px;
  font-style: normal;
  font-weight: 700;
  line-height: 145%; /* 26.1px */
  letter-spacing: -0.36px;
`;

const TableHead = styled.div`
  display: flex;
  padding: 24px 159px 24px 20px;
  flex-direction: row;
  align-items: center;
  gap: 24px;
  align-self: stretch;
  border-radius: 12px;
  background: rgba(112, 115, 124, 0.08);
  div {
    color: #6b7684;
    font-family: 'Spoqa Han Sans Neo';
    font-size: 16px;
    font-style: normal;
    font-weight: 500;
    line-height: 145%; /* 23.2px */
    letter-spacing: -0.32px;
    &:first-child {
      width: 30px;
      flex-shrink: 0;
    }
    &:nth-child(2) {
      display: flex;
      align-items: start;
      gap: 10px;
      flex: 1 0 0;
    }
    &:last-child {
      display: flex;
      width: 320px;
      justify-content: center;
      align-items: center;
      gap: 10px;
    }
  }
`;

const TableList = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 12px;
  align-self: stretch;
`;

const TableItem = styled.div<{ rankIndex: number; currentPage: number }>`
  display: flex;
  padding: 16px;
  align-items: start;
  gap: 12px;
  align-self: stretch;
  border: 1px solid #e1e2e4;
  background: #fff;
  border-radius: 12px;
  flex-direction: column;

  ${({ rankIndex, currentPage }) =>
    currentPage === 1 &&
    rankIndex === 1 &&
    `
    border: 1px solid #ffc803;
    background: rgba(255, 200, 3, 0.08);
  `}
  ${({ rankIndex, currentPage }) =>
    currentPage === 1 &&
    rankIndex === 2 &&
    `
    border: 1px solid #9cc2e4;
    background: rgba(156, 194, 228, 0.12);
  `}
  ${({ rankIndex, currentPage }) =>
    currentPage === 1 &&
    rankIndex === 3 &&
    `
    border: 1px solid #e3b59f;
    background: rgba(227, 181, 159, 0.12);
  `}
`;

const Rank = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: center;
  align-items: center;
  width: 20px;
  height: 20px;
  > img {
    width: 20px;
    height: 20px;
  }
  > span {
    color: #6b7684;

    /* Re_SPOQA/sub_h/med_18 */
    font-family: 'Spoqa Han Sans Neo';
    font-size: 18px;
    font-style: normal;
    font-weight: 500;
    line-height: 145%; /* 26.1px */
    letter-spacing: -0.36px;
  }
`;

const InfoBox = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: start;
  gap: 8px;
  flex: 1 0 0;
  width: 100%;
`;

const TitleInfo = styled.div`
  display: flex;
  gap: 2px;
  color: #333d4b;

  font-family: 'Spoqa Han Sans Neo';
  font-size: 14px;
  font-style: normal;
  font-weight: 700;
  line-height: 145%; /* 20.3px */
  letter-spacing: -0.28px;
`;

const Local = styled.div`
  color: #6b7684;
  font-family: 'Spoqa Han Sans Neo';
  font-size: 12px;
  font-style: normal;
  font-weight: 500;
  line-height: 145%; /* 17.4px */
  letter-spacing: -0.24px;
  display: flex;
  flex-wrap: wrap;
  span {
    color: #333d4b;
    font-family: 'Spoqa Han Sans Neo';
    font-size: 12px;
    font-style: normal;
    font-weight: 700;
    line-height: 145%; /* 17.4px */
    letter-spacing: -0.24px;
  }
`;

const Cicle = styled.div`
  color: #e1e2e4;
  font-size: 14px;
  font-style: normal;
  font-weight: 500;
  letter-spacing: -0.28px;
`;

const TagWrap = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  flex-direction: row;
  align-self: stretch;
  flex-wrap: wrap;
`;

const TagRed = styled.div`
  display: flex;
  padding: 2px 5px;
  justify-content: center;
  align-items: center;
  border-radius: 4px;
  background: #fff0f0;
  color: #f04452;

  font-size: 12px;
  font-style: normal;
  font-weight: 500;
  line-height: 145%; /* 17.4px */
  letter-spacing: -0.24px;
`;

const TagBlue = styled.div`
  display: flex;
  padding: 2px 5px;
  justify-content: center;
  align-items: center;
  border-radius: 4px;
  background: #e7f0fe;
  color: #008aff;

  font-size: 12px;
  font-style: normal;
  font-weight: 500;
  line-height: 145%; /* 17.4px */
  letter-spacing: -0.24px;
`;

const TagGray = styled.div`
  display: flex;
  padding: 2px 5px;
  justify-content: center;
  align-items: center;
  border-radius: 4px;
  background: #f4f5f9;

  color: #4e5968;
  font-size: 12px;
  font-style: normal;
  font-weight: 500;
  line-height: 145%; /* 17.4px */
  letter-spacing: -0.24px;
`;

const Lable = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  span {
    color: #333d4b;
    font-size: 12px;
    font-style: normal;
    font-weight: 500;
    line-height: 145%; /* 23.2px */
    letter-spacing: -0.32px;
  }
  width: 100%;
`;
interface MoveBtnProps {
  rankIndex: number;
}

const MoveBtn = styled.div<MoveBtnProps>`
  display: flex;
  padding: 4px 8px;
  justify-content: center;
  align-items: center;
  gap: 5px;
  align-self: stretch;
  flex-direction: row;
  color: rgba(0, 12, 30, 0.8);
  border-radius: 8px;
  font-family: 'Spoqa Han Sans Neo';
  font-size: 14px;
  font-style: normal;
  font-weight: 500;
  line-height: 145%; /* 20.3px */
  letter-spacing: -0.28px;
  cursor: pointer;
  ${({ rankIndex }) => {
    if (rankIndex === 1) {
      return `
        background: #FFC803;
      `;
    } else if (rankIndex === 2) {
      return `
        background: #9CC2E4;
      `;
    } else if (rankIndex === 3) {
      return `
        background: #B6917F; 
        color: #fff;
      `;
    } else {
      return `
        background: #E8ECEF;
      `;
    }
  }}
`;

const PageWrap = styled.div`
  flex-direction: row;
  justify-content: center;
  width: 100%;
  > div {
    margin: 0;
  }
`;

const Result = styled.div`
  display: flex;
  padding: 2px 5px;
  justify-content: center;
  align-items: center;
  gap: 2px;
  border-radius: 4px;
  background: rgba(2, 32, 71, 0.05);
  color: #4e5968;
  font-size: 12px;
  font-style: normal;
  font-weight: 700;
  line-height: 145%; /* 17.4px */
  letter-spacing: -0.24px;
`;
const FlexBox = styled.div`
  display: flex;
  flex-direction: row;
  width: 100%;
  justify-content: space-between;
`;
const RankIndex = styled.div`
  display: flex;
  width: 20px;
  height: 20px;
  justify-content: center;
  align-items: center;
  gap: 10px;
  border-radius: 999px;
  background: #f6f7f9;
  color: #6b7684;
  font-family: 'Spoqa Han Sans Neo';
  font-size: 12px;
  font-style: normal;
  font-weight: 700;
  line-height: 145%; /* 17.4px */
  letter-spacing: -0.24px;
`;

const FlexBox02 = styled.div`
  display: flex;
  gap: 4px;
  flex-direction: column;
`;
